/**
 * Client API mem.pm — v3, PocketBase JS SDK natif
 *
 * Architecture :
 *   - Auth       → méthodes natives du SDK (authWithPassword, requestPasswordReset…)
 *   - Boards / Messages / Members / Photos → pb.collection().getList() / create() / delete()
 *   - Display / Activity → routes custom Go (/api/display/, /api/activity/)
 *
 * Le SDK injecte automatiquement le token de pb.authStore dans chaque requête.
 * RecordModel est un objet JS plain — accès via record["field"], pas record.get().
 */

import { ClientResponseError, type RecordModel } from "pocketbase";
import { pb } from "./pb";
import { buildUserFileAvatarUrl } from "./avatars";
import {
  DEFAULT_IMAGE_DURATION_MS,
  DEFAULT_MESSAGE_DURATION_MS,
  type CreateWeatherCardInput,
  type GeocodeResult,
  type WeatherCard,
  type WeatherLayout,
  type WeatherStyle,
} from "@mem/shared";

// ─── Query Keys ──────────────────────────────────────────────────

export const queryKeys = {
  boards: ["boards"] as const,
  board: (slug: string) => ["boards", slug] as const,
  messages: (slug: string) => ["boards", slug, "messages"] as const,
  photos: (slug: string) => ["boards", slug, "photos"] as const,
  members: (slug: string) => ["boards", slug, "members"] as const,
  activity: ["activity"] as const,
  boardHistory: (slug: string) => ["boards", slug, "history"] as const,
  display: (slug: string) => ["display", slug] as const,
  weatherCards: (slug: string) => ["boards", slug, "weather-cards"] as const,
  me: ["me"] as const,
};

// ─── Helpers compatibilité ────────────────────────────────────────

/** @deprecated Utiliser pb.authStore.token directement */
export function getToken(): string | null {
  return pb.authStore.token || null;
}

/** @deprecated no-op — le SDK gère le token */
export function setToken(_token: string): void {}

/** @deprecated Utiliser pb.authStore.clear() directement */
export function clearToken(): void {
  pb.authStore.clear();
}

export function isAuthenticated(): boolean {
  return pb.authStore.isValid;
}

// ─── URL assets ──────────────────────────────────────────────────

/**
 * Résout l'URL d'un fichier PocketBase.
 * Utilise pb.files.getURL() qui construit l'URL correcte.
 */
export function resolveFileUrl(record: RecordModel, filename: string): string {
  if (!filename) return "";
  return pb.files.getURL(record, filename);
}

/**
 * Résout l'URL de l'avatar d'un user.
 *
 * Mince façade autour de `buildUserFileAvatarUrl` (lib/avatars.ts) — gardée
 * exportée depuis `api.ts` pour la compatibilité avec les anciens call sites.
 * Retourne `""` (et non `null`) pour ne pas casser le contrat précédent.
 */
export function resolveAvatarUrl(user: { id: string; avatar?: string | null }): string {
  return buildUserFileAvatarUrl(user) ?? "";
}

/**
 * Construit l'URL absolue d'un asset avec ?auth=token pour les <img src>.
 */
export function resolveAssetUrl(url: string, token?: string | null): string {
  if (!url) return url;
  if (url.startsWith("data:") || /^https?:\/\//i.test(url)) return url;
  const base = `${pb.baseURL}${url.startsWith("/") ? url : `/${url}`}`;
  const tok = token !== undefined ? token : getToken();
  if (!tok) return base;
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}auth=${encodeURIComponent(tok)}`;
}

// ─── Discriminated union ──────────────────────────────────────────

type ApiSuccess<T> = { ok: true; data: T; status: number };
type ApiError = { ok: false; error: string; status?: number; details?: unknown };
export type ApiResult<T> = ApiSuccess<T> | ApiError;

function mapError(e: unknown): ApiError {
  if (e instanceof ClientResponseError) {
    // Token expiré ou invalide → logout automatique
    if (e.status === 401) {
      pb.authStore.clear();
    }

    // Erreurs de validation champ par champ (ex: email déjà utilisé)
    const fieldErrors = e.response?.data as Record<string, { message?: string }> | undefined;
    if (fieldErrors && typeof fieldErrors === "object") {
      const msgs = Object.entries(fieldErrors)
        .map(([field, err]) => {
          const raw = err?.message ?? "";
          if (field === "email" && raw.includes("unique")) return "Cet email est déjà utilisé.";
          if (field === "email" && raw.includes("valid")) return "Adresse email invalide.";
          if (field === "password") return "Mot de passe invalide.";
          return raw || `${field}: erreur`;
        })
        .filter(Boolean);
      if (msgs.length) return { ok: false, error: msgs.join(" "), status: e.status, details: e.response };
    }

    const msg =
      e.response?.message ||
      (e.status === 401 ? "Session expirée, veuillez vous reconnecter." :
       e.status === 403 ? "Accès refusé" :
       e.status === 404 ? "Ressource introuvable" :
       e.status >= 500 ? "Erreur serveur" :
       e.message);
    return { ok: false, error: msg, status: e.status, details: e.response };
  }
  return { ok: false, error: e instanceof Error ? e.message : "Network error" };
}

function ok<T>(data: T): ApiSuccess<T> {
  return { ok: true, data, status: 200 };
}

// ─── Types ───────────────────────────────────────────────────────

export interface AuthResponse {
  ok: boolean;
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
    preferred_display_board?: string | null;
  };
}

export interface Board {
  id: string;
  name: string;
  slug: string;
  description: string;
  public: boolean;
  settings: {
    slideInterval: number;
    theme: "light" | "dark" | "warm";
    nightModeStart: string;
    nightModeEnd: string;
    timezone: string;
    language: "fr" | "en" | "nl";
    messageDurationMs: number;
    imageDurationMs: number;
    weatherDurationMs: number;
    display_token?: string;
  };
  role: "admin" | "contributor" | "recipient" | null;
  member_count: number;
  created: string;
  updated: string;
}

export interface Message {
  id: string;
  board_id: string;
  text: string;
  type: string;
  author_name: string;
  created: string;
}

export interface Photo {
  id: string;
  board_id: string;
  caption: string;
  image_url: string;
  file: string;
  author_name: string;
  created: string;
  _record?: RecordModel; // pour resolveFileUrl
}

export interface Member {
  id: string;
  board_id: string;
  user_id: string;
  email: string;
  name: string;
  role: "admin" | "contributor" | "recipient";
  created: string;
}

export interface BoardHistoryMessageItem {
  id: string;
  kind: "message";
  board_id: string;
  author_name: string;
  created: string;
  message: { text: string; type: string };
}

export interface BoardHistoryPhotoItem {
  id: string;
  kind: "photo";
  board_id: string;
  author_name: string;
  created: string;
  photo: { caption: string; image_url: string };
}

export type BoardHistoryItem = BoardHistoryMessageItem | BoardHistoryPhotoItem;

export interface ActivityItem {
  id: string;
  type: string;
  board_id: string;
  board_name: string;
  description: string;
  created: string;
}

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  preferred_display_board?: string | null;
}

// ─── Mappers RecordModel → types métier ──────────────────────────

function recordToBoard(r: RecordModel, userId?: string): Board {
  // role : chercher dans board_members expand ou champ virtuel
  const role = (r["role"] ?? r.expand?.["board_members_via_board"]?.[0]?.["role"] ?? null) as Board["role"];
  return {
    id: r.id,
    name: r["name"] as string,
    slug: r["slug"] as string,
    description: (r["description"] as string) ?? "",
    public: (r["public"] as boolean) ?? false,
    settings: (r["settings"] as Board["settings"]) ?? {
      slideInterval: 5,
      theme: "light",
      nightModeStart: "22:00",
      nightModeEnd: "07:00",
      timezone: "Europe/Paris",
      language: "fr",
    },
    role,
    member_count: (r["member_count"] as number) ?? 0,
    created: r.created,
    updated: r.updated,
  };
}

function recordToMessage(r: RecordModel): Message {
  return {
    id: r.id,
    board_id: r["board"] as string,
    text: r["text"] as string,
    type: (r["type"] as string) ?? "custom",
    author_name: (r.expand?.["user"]?.["name"] as string) ?? (r["author_name"] as string) ?? "",
    created: r.created,
  };
}

function recordToPhoto(r: RecordModel): Photo {
  const filename = r["file"] as string;
  return {
    id: r.id,
    board_id: r["board"] as string,
    caption: (r["caption"] as string) ?? "",
    file: filename,
    image_url: filename ? pb.files.getURL(r, filename) : "",
    author_name: (r.expand?.["user"]?.["name"] as string) ?? (r["author_name"] as string) ?? "",
    created: r.created,
    _record: r,
  };
}

function recordToMember(r: RecordModel): Member {
  const user = r.expand?.["user"] as RecordModel | undefined;
  return {
    id: r.id,
    board_id: r["board"] as string,
    user_id: r["user"] as string,
    email: (user?.["email"] as string) ?? "",
    name: (user?.["name"] as string) ?? "",
    role: (r["role"] as Member["role"]) ?? "contributor",
    created: r.created,
  };
}

function recordToUser(r: RecordModel): UserProfile {
  return {
    id: r.id,
    email: r["email"] as string,
    name: r["name"] as string,
    avatar: r["avatar"] as string | undefined,
    preferred_display_board: (r["preferred_display_board"] as string | undefined) ?? null,
  };
}

// ─── Auth — SDK natif ─────────────────────────────────────────────

export async function login(email: string, password: string): Promise<ApiResult<AuthResponse>> {
  try {
    const auth = await pb.collection("users").authWithPassword(email, password);
    return ok({
      ok: true,
      token: auth.token,
      user: {
        id: auth.record.id,
        email: auth.record["email"] as string,
        name: auth.record["name"] as string,
        preferred_display_board:
          (auth.record["preferred_display_board"] as string | undefined) ?? null,
      },
    });
  } catch (e) {
    return mapError(e);
  }
}

export async function register(
  email: string,
  password: string,
  name: string,
): Promise<ApiResult<AuthResponse>> {
  try {
    await pb.collection("users").create({ email, password, passwordConfirm: password, name });
    const auth = await pb.collection("users").authWithPassword(email, password);
    return ok({
      ok: true,
      token: auth.token,
      user: {
        id: auth.record.id,
        email: auth.record["email"] as string,
        name: auth.record["name"] as string,
        preferred_display_board:
          (auth.record["preferred_display_board"] as string | undefined) ?? null,
      },
    });
  } catch (e) {
    return mapError(e);
  }
}

export async function forgotPassword(email: string): Promise<ApiResult<{ message: string }>> {
  try {
    await pb.collection("users").requestPasswordReset(email);
    return ok({ message: "Email envoyé" });
  } catch (e) {
    return mapError(e);
  }
}

export async function resetPassword(
  token: string,
  password: string,
): Promise<ApiResult<{ message: string }>> {
  try {
    await pb.collection("users").confirmPasswordReset(token, password, password);
    return ok({ message: "Mot de passe réinitialisé" });
  } catch (e) {
    return mapError(e);
  }
}

// ─── Me ──────────────────────────────────────────────────────────

export async function getMe(): Promise<ApiResult<{ user: UserProfile }>> {
  try {
    const auth = await pb.collection("users").authRefresh();
    return ok({ user: recordToUser(auth.record) });
  } catch (e) {
    return mapError(e);
  }
}

export async function updateProfileName(name: string): Promise<ApiResult<{ user: UserProfile }>> {
  try {
    const userId = pb.authStore.record?.id;
    if (!userId) throw new Error("Non authentifié");
    const r = await pb.collection("users").update(userId, { name });
    return ok({ user: recordToUser(r) });
  } catch (e) {
    return mapError(e);
  }
}

export async function uploadAvatar(file: File): Promise<ApiResult<{ user: UserProfile }>> {
  try {
    const userId = pb.authStore.record?.id;
    if (!userId) throw new Error("Non authentifié");
    const formData = new FormData();
    formData.append("avatar", file);
    const r = await pb.collection("users").update(userId, formData);
    return ok({ user: recordToUser(r) });
  } catch (e) {
    return mapError(e);
  }
}

export async function updatePreferredDisplayBoard(
  boardId: string | null,
): Promise<ApiResult<{ preferred_display_board: string | null; preferred_display_slug: string | null }>> {
  try {
    const data = await pb.send<{
      ok: boolean;
      preferred_display_board: string | null;
      preferred_display_slug: string | null;
    }>("/api/me/preferences/display-board", {
      method: "PUT",
      body: { boardId },
    });

    return ok({
      preferred_display_board: data.preferred_display_board ?? null,
      preferred_display_slug: data.preferred_display_slug ?? null,
    });
  } catch (e) {
    return mapError(e);
  }
}

// ─── Boards ──────────────────────────────────────────────────────

export async function getBoards(): Promise<ApiResult<{ boards: Board[] }>> {
  try {
    const userId = pb.authStore.record?.id;
    if (!userId) return ok({ boards: [] });

    // Récupère les boards où l'user est membre, avec le rôle
    const members = await pb.collection("board_members").getList(1, 200, {
      filter: `user = '${userId}'`,
      expand: "board",
      sort: "-created",
    });

    const boards = members.items
      .filter((m) => m.expand?.["board"])
      .map((m) => {
        const b = m.expand!["board"] as RecordModel;
        return recordToBoard({ ...b, role: m["role"] });
      });

    return ok({ boards });
  } catch (e) {
    return mapError(e);
  }
}

export async function getBoard(slug: string): Promise<ApiResult<{ board: Board }>> {
  try {
    const r = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    const userId = pb.authStore.record?.id;
    let role: Board["role"] = null;
    if (userId) {
      try {
        const m = await pb.collection("board_members").getFirstListItem(
          `board = '${r.id}' && user = '${userId}'`,
        );
        role = m["role"] as Board["role"];
      } catch {}
    }
    return ok({ board: recordToBoard({ ...r, role }) });
  } catch (e) {
    return mapError(e);
  }
}

export async function createBoard(input: {
  name: string;
  slug: string;
  description?: string;
  public?: boolean;
}): Promise<ApiResult<{ board: Board }>> {
  try {
    const r = await pb.collection("boards").create({
      ...input,
      owner: pb.authStore.record?.id,
      settings: {
        slideInterval: 5,
        theme: "light",
        nightModeStart: "22:00",
        nightModeEnd: "07:00",
        timezone: "Europe/Paris",
        language: "fr",
        messageDurationMs: DEFAULT_MESSAGE_DURATION_MS,
        imageDurationMs: DEFAULT_IMAGE_DURATION_MS,
      },
    });
    return ok({ board: recordToBoard({ ...r, role: "admin" }) });
  } catch (e) {
    return mapError(e);
  }
}

export async function updateBoard(
  slug: string,
  input: Partial<Pick<Board, "name" | "description" | "public" | "settings">>,
): Promise<ApiResult<{ board: Board }>> {
  try {
    const existing = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    const r = await pb.collection("boards").update(existing.id, input);
    return ok({ board: recordToBoard(r) });
  } catch (e) {
    return mapError(e);
  }
}

export async function deleteBoard(slug: string): Promise<ApiResult<{ message: string }>> {
  try {
    const r = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    await pb.collection("boards").delete(r.id);
    return ok({ message: "Board supprimé" });
  } catch (e) {
    return mapError(e);
  }
}

// ─── Messages ────────────────────────────────────────────────────

export async function getMessages(slug: string): Promise<ApiResult<{ messages: Message[] }>> {
  try {
    const board = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    const list = await pb.collection("messages").getList(1, 200, {
      filter: `board = '${board.id}'`,
      expand: "user",
      sort: "-created",
    });
    return ok({ messages: list.items.map(recordToMessage) });
  } catch (e) {
    return mapError(e);
  }
}

export async function createMessage(
  slug: string,
  text: string,
  type = "custom",
): Promise<ApiResult<{ message: Message }>> {
  try {
    const board = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    const r = await pb.collection("messages").create({
      board: board.id,
      text,
      type,
      user: pb.authStore.record?.id,
    });
    return ok({ message: recordToMessage(r) });
  } catch (e) {
    return mapError(e);
  }
}

export async function deleteMessage(
  slug: string,
  msgId: string,
): Promise<ApiResult<{ message: string }>> {
  try {
    await pb.collection("messages").delete(msgId);
    return ok({ message: "Message supprimé" });
  } catch (e) {
    return mapError(e);
  }
}

// ─── Photos ──────────────────────────────────────────────────────

export async function getPhotos(
  slug: string,
  page = 1,
  perPage = 50,
): Promise<
  ApiResult<{
    photos: Photo[];
    page: number;
    perPage: number;
    totalItems: number;
    totalPages: number;
  }>
> {
  try {
    const board = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    const list = await pb.collection("photos").getList(page, perPage, {
      filter: `board = '${board.id}'`,
      expand: "user",
      sort: "-created",
    });
    return ok({
      photos: list.items.map(recordToPhoto),
      page: list.page,
      perPage: list.perPage,
      totalItems: list.totalItems,
      totalPages: list.totalPages,
    });
  } catch (e) {
    return mapError(e);
  }
}

export async function uploadPhoto(
  slug: string,
  file: File,
  caption?: string,
): Promise<ApiResult<{ photo: Photo }>> {
  try {
    const board = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("board", board.id);
    formData.append("user", pb.authStore.record?.id ?? "");
    if (caption) formData.append("caption", caption);
    const r = await pb.collection("photos").create(formData);
    return ok({ photo: recordToPhoto(r) });
  } catch (e) {
    return mapError(e);
  }
}

export async function deletePhoto(
  slug: string,
  photoId: string,
): Promise<ApiResult<{ message: string }>> {
  try {
    await pb.collection("photos").delete(photoId);
    return ok({ message: "Photo supprimée" });
  } catch (e) {
    return mapError(e);
  }
}

export async function promoteItem(
  collection: string,
  itemId: string,
): Promise<ApiResult<{ ok: boolean; promoted_at: string }>> {
  try {
    const now = new Date().toISOString();
    await pb.collection(collection).update(itemId, { promoted_at: now });
    return ok({ ok: true, promoted_at: now });
  } catch (e) {
    return mapError(e);
  }
}

export function promotePhoto(
  _slug: string,
  photoId: string,
): Promise<ApiResult<{ ok: boolean; promoted_at: string }>> {
  return promoteItem("photos", photoId);
}

/**
 * Promeut une carte météo en tête de la display_queue.
 * Si `layout` est fourni, le champ layout de la carte est également mis à jour
 * au moment de la promotion (changement de layout sans recréer la carte).
 */
export async function promoteWeatherCard(
  cardId: string,
  layout?: string,
): Promise<ApiResult<{ ok: boolean; promoted_at: string }>> {
  try {
    const now = new Date().toISOString();
    const update: Record<string, unknown> = { promoted_at: now };
    if (layout) update.layout = layout;
    await pb.collection("weather_cards").update(cardId, update);
    return ok({ ok: true, promoted_at: now });
  } catch (e) {
    return mapError(e);
  }
}

// ─── Members ─────────────────────────────────────────────────────

export async function getMembers(slug: string): Promise<ApiResult<{ members: Member[] }>> {
  try {
    const board = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    const list = await pb.collection("board_members").getList(1, 200, {
      filter: `board = '${board.id}'`,
      expand: "user",
      sort: "created",
    });
    return ok({ members: list.items.map(recordToMember) });
  } catch (e) {
    return mapError(e);
  }
}

export async function addMember(
  slug: string,
  email: string,
  role = "contributor",
): Promise<ApiResult<{ member: Member }>> {
  try {
    const board = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    const user = await pb.collection("users").getFirstListItem(`email = '${email}'`);
    const r = await pb.collection("board_members").create({
      board: board.id,
      user: user.id,
      role,
    }, { expand: "user" });
    return ok({ member: recordToMember(r) });
  } catch (e) {
    return mapError(e);
  }
}

export async function removeMember(
  slug: string,
  memberId: string,
): Promise<ApiResult<{ message: string }>> {
  try {
    await pb.collection("board_members").delete(memberId);
    return ok({ message: "Membre retiré" });
  } catch (e) {
    return mapError(e);
  }
}

// ─── Activity (route custom Go) ───────────────────────────────────

export async function getActivity(): Promise<ApiResult<{ items: ActivityItem[] }>> {
  try {
    const data = await pb.send<{ items: ActivityItem[] }>("/api/activity/list", {});
    return ok(data);
  } catch (e) {
    return mapError(e);
  }
}

export async function getBoardHistory(
  slug: string,
): Promise<ApiResult<{ items: BoardHistoryItem[] }>> {
  try {
    // Historique = messages + photos du board, triés par date
    const board = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    const [msgs, photos] = await Promise.all([
      pb.collection("messages").getList(1, 100, {
        filter: `board = '${board.id}'`,
        expand: "user",
        sort: "-created",
      }),
      pb.collection("photos").getList(1, 100, {
        filter: `board = '${board.id}'`,
        expand: "user",
        sort: "-created",
      }),
    ]);

    const items: BoardHistoryItem[] = [
      ...msgs.items.map((r): BoardHistoryMessageItem => ({
        id: r.id,
        kind: "message",
        board_id: r["board"] as string,
        author_name: (r.expand?.["user"]?.["name"] as string) ?? "",
        created: r.created,
        message: { text: r["text"] as string, type: r["type"] as string },
      })),
      ...photos.items.map((r): BoardHistoryPhotoItem => ({
        id: r.id,
        kind: "photo",
        board_id: r["board"] as string,
        author_name: (r.expand?.["user"]?.["name"] as string) ?? "",
        created: r.created,
        photo: { caption: r["caption"] as string, image_url: recordToPhoto(r).image_url },
      })),
    ].sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());

    return ok({ items });
  } catch (e) {
    return mapError(e);
  }
}

// ─── Display (route custom Go) ────────────────────────────────────

export const getDisplayFeed = (slug: string, token?: string) => {
  const qs = token ? `?auth=${encodeURIComponent(token)}` : "";
  return pb.send<unknown>(`/api/display/${slug}${qs}`, {}).catch(mapError);
};

export async function generateDisplayToken(
  slug: string,
): Promise<ApiResult<{ display_token: string }>> {
  try {
    const data = await pb.send<{ display_token: string }>(
      `/api/display/${slug}/token`,
      { method: "POST" },
    );
    return ok(data);
  } catch (e) {
    return mapError(e);
  }
}

// ─── Weather cards ────────────────────────────────────────────────

function recordToWeatherCard(r: RecordModel): WeatherCard {
  return {
    id: r.id,
    board_id: r["board"],
    city_query: r["city_query"] ?? "",
    city_label: r["city_label"] ?? "",
    country_code: r["country_code"] || undefined,
    latitude: Number(r["latitude"]),
    longitude: Number(r["longitude"]),
    timezone: r["timezone"] ?? "",
    layout: r["layout"] as WeatherLayout,
    style: (r["style"] as WeatherStyle) || "warm",
    display_duration_ms: r["display_duration_ms"] || undefined,
    created: r.created,
    updated: r.updated,
  };
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

export async function listWeatherCards(
  slug: string,
): Promise<ApiResult<{ cards: WeatherCard[] }>> {
  try {
    const board = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    const list = await pb.collection("weather_cards").getList(1, 100, {
      filter: `board = '${board.id}'`,
      sort: "-created",
    });
    return ok({ cards: list.items.map(recordToWeatherCard) });
  } catch (e) {
    return mapError(e);
  }
}

export async function createWeatherCard(
  slug: string,
  input: CreateWeatherCardInput,
): Promise<ApiResult<{ card: WeatherCard; reused: boolean }>> {
  try {
    const board = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    const lat = round3(input.latitude);
    const lon = round3(input.longitude);
    const payload = {
      board: board.id,
      user: pb.authStore.record?.id,
      city_query: input.city_query,
      city_label: input.city_label,
      country_code: input.country_code ?? "",
      latitude: lat,
      longitude: lon,
      timezone: input.timezone,
      layout: input.layout,
      style: input.style ?? "warm",
      display_duration_ms: input.display_duration_ms ?? 0,
    };

    // Dedup: lookup by (board, lat≈3dec, lon≈3dec, layout). Use a small epsilon
    // band so historical records stored with extra precision still match.
    const eps = 0.0005;
    const filter =
      `board = '${board.id}'` +
      ` && layout = '${input.layout}'` +
      ` && latitude >= ${lat - eps} && latitude <= ${lat + eps}` +
      ` && longitude >= ${lon - eps} && longitude <= ${lon + eps}`;

    let existing: RecordModel | null = null;
    try {
      existing = await pb.collection("weather_cards").getFirstListItem(filter);
    } catch (e) {
      if (!(e instanceof ClientResponseError) || e.status !== 404) throw e;
    }

    if (existing) {
      const updated = await pb.collection("weather_cards").update(existing.id, {
        style: payload.style,
        user: payload.user,
        city_label: payload.city_label,
        city_query: payload.city_query,
        country_code: payload.country_code,
        timezone: payload.timezone,
        display_duration_ms: payload.display_duration_ms,
      });
      return ok({ card: recordToWeatherCard(updated), reused: true });
    }

    const created = await pb.collection("weather_cards").create(payload);
    return ok({ card: recordToWeatherCard(created), reused: false });
  } catch (e) {
    return mapError(e);
  }
}

export async function deleteWeatherCard(
  cardId: string,
): Promise<ApiResult<{ message: string }>> {
  try {
    await pb.collection("weather_cards").delete(cardId);
    return ok({ message: "Carte météo supprimée" });
  } catch (e) {
    return mapError(e);
  }
}

export async function geocodeCity(
  q: string,
  lang?: string,
): Promise<ApiResult<{ results: GeocodeResult[] }>> {
  try {
    const params = new URLSearchParams({ q });
    if (lang) params.set("lang", lang);
    const data = await pb.send<{ ok: boolean; results: GeocodeResult[] }>(
      `/api/weather/geocode?${params.toString()}`,
      {},
    );
    return ok({ results: data.results ?? [] });
  } catch (e) {
    return mapError(e);
  }
}

// ─── Display Queue ────────────────────────────────────────────────

export type QueueRecord = {
  id: string;          // display_queue record ID
  position: number;
  content_type: "photo" | "message" | "weather_card";
  content_id: string;
  data: Record<string, unknown>;
  created: string;
};

/**
 * Récupère la file d'affichage du jour via le feed Go.
 * On réutilise l'endpoint /api/display/:slug qui résout déjà les contenus.
 */
export async function getDisplayQueue(slug: string): Promise<ApiResult<{ queue: QueueRecord[]; archive_count: number }>> {
  try {
    const data = await pb.send<{ ok: boolean; feed?: { queue: QueueRecord[]; archive: unknown[] } }>(
      `/api/display/${slug}`,
      { method: "GET" },
    );
    if (!data.ok || !data.feed) return { ok: false, error: "Impossible de charger la file" };
    return ok({
      queue: data.feed.queue,
      archive_count: (data.feed.archive as unknown[]).length,
    });
  } catch (e) {
    return mapError(e);
  }
}

/** Met à jour la position d'un item dans display_queue. */
export async function updateQueuePosition(itemId: string, position: number): Promise<ApiResult<void>> {
  try {
    await pb.collection("display_queue").update(itemId, { position });
    return ok(undefined);
  } catch (e) {
    return mapError(e);
  }
}

/** Retire un item de la file (sans supprimer le contenu original). */
export async function removeFromQueue(itemId: string): Promise<ApiResult<void>> {
  try {
    await pb.collection("display_queue").delete(itemId);
    return ok(undefined);
  } catch (e) {
    return mapError(e);
  }
}

// ─── Weather Favorites ────────────────────────────────────────────

import {
  addFavorite,
  removeFavorite,
  getFavoritesFromSettings,
  type WeatherFavorite,
} from "./weather-favorites";

export type { WeatherFavorite };

/**
 * Lit les favoris depuis board.settings.
 * Pas de requête séparée — dérivé de getBoard().
 */
export async function getWeatherFavorites(
  slug: string,
): Promise<ApiResult<{ favorites: WeatherFavorite[] }>> {
  try {
    const board = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    const settings = (board["settings"] as Record<string, unknown>) ?? {};
    return ok({ favorites: getFavoritesFromSettings(settings) });
  } catch (e) {
    return mapError(e);
  }
}

/**
 * Ajoute un favori dans board.settings.weatherFavorites (read-modify-write).
 */
export async function addWeatherFavorite(
  slug: string,
  favorite: WeatherFavorite,
): Promise<ApiResult<void>> {
  try {
    const board = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    const settings = (board["settings"] as Record<string, unknown>) ?? {};
    const current = getFavoritesFromSettings(settings);
    const updated = addFavorite(current, favorite);
    await pb.collection("boards").update(board.id, {
      settings: { ...settings, weatherFavorites: updated },
    });
    return ok(undefined);
  } catch (e) {
    return mapError(e);
  }
}

/**
 * Retire un favori par son id (read-modify-write).
 */
export async function removeWeatherFavorite(
  slug: string,
  favoriteId: string,
): Promise<ApiResult<void>> {
  try {
    const board = await pb.collection("boards").getFirstListItem(`slug = '${slug}'`);
    const settings = (board["settings"] as Record<string, unknown>) ?? {};
    const current = getFavoritesFromSettings(settings);
    const updated = removeFavorite(current, favoriteId);
    await pb.collection("boards").update(board.id, {
      settings: { ...settings, weatherFavorites: updated },
    });
    return ok(undefined);
  } catch (e) {
    return mapError(e);
  }
}
